import type { ReactNode } from 'react';
import type { Pareja } from '../services/api';
import type { GrupoConTabla } from '../nucleo/vistaGrupos';
import { ganador, type Marcador } from '../nucleo/marcador';
import { textoDeHorario } from '../nucleo/programacion';
import { Tarjeta, Titulo } from './ui';

// La tabla de un grupo y sus partidos. La misma pieza para el público y para
// el organizador (que le suma, por partido, el botón para cargar el resultado).
// La cara sale de la maqueta (maqueta/index.html).

export const nombrePareja = (p: Pareja | undefined) => (p ? `${p.jugador1} / ${p.jugador2}` : '—');
/** Para los motivos de desempate: "le ganó a Molina". */
const apellido = (p: Pareja | undefined) => p?.jugador1 ?? '';

const signo = (n: number) => (n > 0 ? `+${n}` : `${n}`);

export function VistaGrupo({
  grupo,
  parejas,
  pasan,
  extraPartido,
  pie,
  plegable = false,
  conHorarios = true,
}: {
  grupo: GrupoConTabla;
  parejas: Map<number, Pareja>;
  pasan: number;
  /** Lo que va debajo de cada partido (el admin pone ahí "Cargar resultado"). */
  extraPartido?: (partidoId: number) => ReactNode;
  pie?: ReactNode;
  plegable?: boolean;
  conHorarios?: boolean;
}) {
  const { tabla } = grupo;
  const jugados = grupo.partidos.filter((p) => grupo.resultados.get(p.id)?.marcador).length;
  const estado = tabla.completo ? 'terminado' : `${jugados} de ${grupo.partidos.length} jugados`;

  const contenido = (
    <>
      <table className="w-full border-collapse text-sm tabular-nums">
        <thead>
          <tr className="text-[11px] uppercase text-(--color-apagado)">
            <th className="w-6" />
            <th className="py-1 text-left font-semibold">Pareja</th>
            <th className="w-7 font-semibold">PJ</th>
            <th className="w-7 font-semibold">G</th>
            <th className="w-10 font-semibold">Sets</th>
            <th className="w-12 font-semibold">Games</th>
          </tr>
        </thead>
        <tbody>
          {tabla.filas.map((f, i) => {
            const pasa = tabla.clasificados.includes(f.id);
            // Cómo se desempató, solo con el grupo terminado: a mitad de camino,
            // "dif. de sets 0" debajo de una pareja que no jugó confunde.
            const motivo = !tabla.completo ? null : f.motivo === 'le ganó' ? `le ganó a ${apellido(parejas.get(f.leGanoA!))}` : f.motivo;
            return (
              <tr key={f.id} className="border-t border-(--color-borde)">
                <td
                  className={`py-2 text-center ${pasa ? 'font-bold text-(--color-acento)' : 'text-(--color-apagado)'}`}
                  style={pasa ? { boxShadow: 'inset 3px 0 0 var(--color-acento)' } : undefined}
                >
                  {i + 1}
                </td>
                <td className={`py-2 font-semibold ${f.abandono ? 'text-(--color-apagado) line-through' : ''}`}>
                  {nombrePareja(parejas.get(f.id))}
                  {f.abandono && <span className="block text-[11px] font-normal no-underline">abandonó</span>}
                  {!f.abandono && motivo && <span className="block text-[11px] font-normal text-(--color-apagado)">{motivo}</span>}
                </td>
                <td className="text-center">{f.pj}</td>
                <td className="text-center font-bold">{f.pg}</td>
                <td className="text-center">{signo(f.setsFavor - f.setsContra)}</td>
                <td className="text-center">{signo(f.gamesFavor - f.gamesContra)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {tabla.faltaSorteo.length > 0 && (
        <p className="mt-2 text-xs text-(--color-alerta)">
          Hay parejas iguales en todo: el orden se define por sorteo.
        </p>
      )}
      {!tabla.completo && <p className="mt-2 text-xs text-(--color-apagado)">Pasan {pasan} por grupo: se marcan cuando termina el grupo.</p>}

      {plegable && <p className="mt-3 text-xs text-(--color-apagado)">Abrí los partidos para ver resultados{conHorarios ? ', horarios y canchas' : ''}.</p>}
      <SeccionPartidos plegable={plegable} cantidad={grupo.partidos.length} conHorarios={conHorarios}>
      <ul className="flex flex-col">
        {grupo.partidos.map((p) => {
          const r = grupo.resultados.get(p.id);
          return (
            <li key={p.id} className="border-t border-(--color-borde) py-2 first:border-t-0">
              <FilaPartido
                a={parejas.get(p.pareja_a!)}
                b={parejas.get(p.pareja_b!)}
                marcador={r?.marcador ?? null}
                porAbandono={r?.porAbandono ?? false}
                cuando={textoDeHorario(p)}
                enJuego={p.estado !== 'jugado' && p.en_juego_desde !== null}
              />
              {extraPartido?.(p.id)}
            </li>
          );
        })}
      </ul>
      </SeccionPartidos>
      {pie}
    </>
  );
  return (
    <Tarjeta>
      {plegable ? (
        <details className="group/grupo">
          <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 focus-visible:outline-2 focus-visible:outline-(--color-acento)">
            <span><span className="block font-titulo text-2xl uppercase">Grupo {grupo.letra}</span>
              <span className="block text-xs text-(--color-apagado)">{tabla.filas.length} parejas · {estado}</span>
            </span>
            <span aria-hidden="true" className="text-(--color-acento) transition-transform group-open/grupo:rotate-90">▸</span>
          </summary>
          <div className="mt-3">{contenido}</div>
        </details>
      ) : <><Titulo extra={estado}>Grupo {grupo.letra}</Titulo>{contenido}</>}
    </Tarjeta>
  );
}

function SeccionPartidos({ conHorarios, plegable, cantidad, children }: { conHorarios: boolean; plegable: boolean; cantidad: number; children: ReactNode }) {
  if (!plegable) return <div className="mt-3">{children}</div>;
  return <details className="group/partidos mt-3">
    <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-2 rounded-lg border border-(--color-borde) px-3 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-(--color-acento)">
      {conHorarios ? 'Partidos y horarios' : 'Partidos'} · {cantidad}
      <span aria-hidden="true" className="transition-transform group-open/partidos:rotate-90">▸</span>
    </summary>
    {children}
  </details>;
}

/** Dos renglones, uno por pareja, con los games de cada set a la derecha. */
export function FilaPartido({
  a,
  b,
  marcador,
  porAbandono,
  cuando,
  enJuego,
}: {
  a: Pareja | undefined;
  b: Pareja | undefined;
  marcador: Marcador | null;
  porAbandono?: boolean;
  /** "Sáb 18 · 18:00 · Cancha 2", si tiene horario. */
  cuando?: string;
  enJuego?: boolean;
}) {
  const gana = marcador ? ganador(marcador) : null;
  const renglon = (lado: 'a' | 'b', p: Pareja | undefined) => {
    const clase = gana ? (gana === lado ? 'font-bold' : 'text-(--color-apagado)') : '';
    let numeros: ReactNode = null;
    if (marcador?.wo) numeros = gana === lado ? <span className="text-xs">W.O.</span> : null;
    else if (marcador?.sets) {
      const sets = [...marcador.sets, ...(marcador.tb ? [marcador.tb] : [])];
      numeros = sets.map(([x, y], i) => {
        const mio = lado === 'a' ? x : y;
        const suyo = lado === 'a' ? y : x;
        return (
          <span key={i} className={`w-6 text-center ${mio > suyo ? 'font-bold' : ''}`}>
            {mio}
          </span>
        );
      });
    }
    return (
      <div className={`flex justify-between gap-2 text-sm ${clase}`}>
        <span>{nombrePareja(p)}</span>
        <span className="flex tabular-nums">{numeros}</span>
      </div>
    );
  };
  return (
    <div>
      {renglon('a', a)}
      {renglon('b', b)}
      {enJuego ? (
        <p className="text-xs font-bold text-(--color-vivo)">● En juego{cuando ? <span className="font-normal text-(--color-apagado)"> · {cuando}</span> : null}</p>
      ) : (
        (cuando || !marcador) && <p className="text-xs text-(--color-apagado)">{cuando || 'Por jugar'}</p>
      )}
      {porAbandono && <p className="text-xs text-(--color-apagado)">W.O.: la otra pareja abandonó</p>}
    </div>
  );
}
